// src/utils/fetchWrapper.js
const GET_CACHE_TTL_MS = Number(process.env.REACT_APP_GET_CACHE_TTL_MS || 15000);
const GET_CACHE_MAX_ENTRIES = Number(process.env.REACT_APP_GET_CACHE_MAX_ENTRIES || 500);
const _inFlightGetRequests = new Map();
const _getResponseCache = new Map();

export const getStoredOidcToken = () => {
  const key = Object.keys(sessionStorage).find((k) => k.startsWith('oidc.user:'));
  if (!key) return '';
  try {
    const user = JSON.parse(sessionStorage.getItem(key));
    return user?.id_token || '';
  } catch {
    return '';
  }
};

const buildRequestKey = (method, url, headers) => {
  const normalizedHeaders = Object.entries(headers || {})
    .map(([k, v]) => [String(k).toLowerCase(), String(v)])
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([k, v]) => `${k}:${v}`)
    .join('|');
  return `${method}|${url}|${normalizedHeaders}`;
};

const createResponseFromSnapshot = (snapshot) =>
  new Response(snapshot.body, {
    status: snapshot.status,
    statusText: snapshot.statusText,
    headers: snapshot.headers,
  });

const snapshotResponse = async (response) => {
  const clone = response.clone();
  return {
    body: await clone.text(),
    status: clone.status,
    statusText: clone.statusText,
    headers: Object.fromEntries(clone.headers.entries()),
  };
};

const getCachedSnapshot = (cacheKey) => {
  const entry = _getResponseCache.get(cacheKey);
  if (!entry) return null;
  if (entry.expiresAt <= Date.now()) {
    _getResponseCache.delete(cacheKey);
    return null;
  }
  return entry.snapshot;
};

const setCachedSnapshot = (cacheKey, snapshot) => {
  if (GET_CACHE_TTL_MS <= 0) return;
  _getResponseCache.set(cacheKey, {
    snapshot,
    expiresAt: Date.now() + GET_CACHE_TTL_MS,
  });

  while (_getResponseCache.size > GET_CACHE_MAX_ENTRIES) {
    const firstKey = _getResponseCache.keys().next().value;
    if (!firstKey) break;
    _getResponseCache.delete(firstKey);
  }
};

const logAuthWarnings = (statusCode) => {
  if (statusCode === 401) {
    console.warn('Unauthorized (401). Session may have expired.');
  } else if (statusCode === 403) {
    console.warn('Forbidden (403). Access denied for this resource.');
  }
};

const fetchWrapper = async (url, options = {}) => {
  const token = getStoredOidcToken();
  const method = String(options.method || 'GET').toUpperCase();

  const defaultHeaders = {
    ...(token && { Authorization: `Bearer ${token}` }),
  };

  // Keep GET/HEAD "simple" to avoid unnecessary CORS preflight.
  // For mutating requests, default JSON content-type when caller did not set one.
  if (method !== 'GET' && method !== 'HEAD' && options.body !== undefined) {
    defaultHeaders['Content-Type'] = 'application/json';
  }

  const config = {
    ...options,
    headers: {
      ...defaultHeaders,
      ...options.headers,
    },
  };

  const shouldUseGetCache =
    method === 'GET' &&
    GET_CACHE_TTL_MS > 0 &&
    options.skipCache !== true &&
    options.cache !== 'no-store';

  if (!shouldUseGetCache) {
    if (method !== 'GET') {
      // Mutating requests can make previously cached GET payloads stale.
      _getResponseCache.clear();
    }
    try {
      const response = await fetch(url, config);
      if (!response.ok) logAuthWarnings(response.status);
      return response;
    } catch (err) {
      console.error('Fetch error:', err);
      throw err;
    }
  }

  const requestKey = buildRequestKey(method, url, config.headers);

  const cachedSnapshot = getCachedSnapshot(requestKey);
  if (cachedSnapshot) {
    return createResponseFromSnapshot(cachedSnapshot);
  }

  const inFlight = _inFlightGetRequests.get(requestKey);
  if (inFlight) {
    const sharedSnapshot = await inFlight;
    return createResponseFromSnapshot(sharedSnapshot);
  }

  const fetchPromise = (async () => {
    try {
      const response = await fetch(url, config);
      const snapshot = await snapshotResponse(response);
      if (!response.ok) {
        logAuthWarnings(response.status);
      } else {
        setCachedSnapshot(requestKey, snapshot);
      }
      return snapshot;
    } finally {
      _inFlightGetRequests.delete(requestKey);
    }
  })();

  _inFlightGetRequests.set(requestKey, fetchPromise);

  try {
    const snapshot = await fetchPromise;
    return createResponseFromSnapshot(snapshot);
  } catch (err) {
    console.error('Fetch error:', err);
    throw err;
  }
};

export const clearFetchWrapperGetCache = () => {
  _getResponseCache.clear();
  _inFlightGetRequests.clear();
};

export default fetchWrapper;
  
